import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IUnsplashImageQueryPaginated } from 'app/library/image-library/interfaces/unsplash-image-paginated.query.interface';
import { UnsplashImage } from 'app/library/image-library/models/unsplash-image.model';
import { ModuleImage } from 'app/library/module/models/module-image.model';
import { IModuleImageQuery } from 'app/library/module/interfaces/module-image.interface';
import { IModuleQuery } from 'app/library/module/interfaces/module.query.interface';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { UnsplashImageUploadCommand } from 'app/projects/user/src/lib/models/unsplash-image-upload.command';

export interface Pagination {
    numberOfPages: number;
    numberOfResults: number;
}

@Injectable()
export class UnsplashImageApiService {
    private apiUrl: string;
    private _unsplashImages = new BehaviorSubject<UnsplashImage[]>([]);
    public unsplashImages$ = this._unsplashImages.asObservable();
    unsplashImagesPage = 1;
    fetchingUnsplashImages = false;

    private _unsplashImagesPagination: Pagination = { numberOfPages: 0, numberOfResults: 0 };

    get unsplashImagesPagination(): Pagination {
        return this._unsplashImagesPagination;
    }
    set unsplashImagesPagination(value: Pagination) {
        this._unsplashImagesPagination = value;
    }

    constructor(private http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) { apiUrl }: IEnvironment) {
        this.apiUrl = apiUrl;
    }

    getUnsplashImages(page: number, searchTerm?: string): Observable<boolean> {
        this.unsplashImagesPage = page;
        const url = searchTerm ? `${this.apiUrl}/cil/unsplash/photos?page=${page}&search=${searchTerm}` : `${this.apiUrl}/cil/unsplash/photos?page=${page}`;
        return this.http.get<IUnsplashImageQueryPaginated>(url).pipe(
            map((queries) => {
                this.unsplashImagesPagination = {
                    numberOfPages: queries.pagination.number_of_pages,
                    numberOfResults: queries.pagination.number_of_results,
                };
                const unsplashImages = queries.data.map((query) => new UnsplashImage(query));
                this._unsplashImages.next(unsplashImages);
                return true;
            })
        );
    }

    getUnsplashImagesFiltered(page: number, category: string, searchTerm?: string): Observable<boolean> {
        this.unsplashImagesPage = page;
        const url = searchTerm
            ? `${this.apiUrl}/cil/unsplash/photos?page=${page}&filter=${category}&search=${searchTerm}`
            : `${this.apiUrl}/cil/unsplash/photos?page=${page}&filter=${category}`;
        return this.http.get<IUnsplashImageQueryPaginated>(url).pipe(
            map((queries) => {
                this.unsplashImagesPagination = {
                    numberOfPages: queries.pagination.number_of_pages,
                    numberOfResults: queries.pagination.number_of_results,
                };
                const unsplashImages = queries.data.map((query) => new UnsplashImage(query));
                this._unsplashImages.next(unsplashImages);
                return true;
            })
        );
    }

    async addUnplashImagesToTenantLibrary(unsplashImages: { unsplash_photos: { id: string }[] }): Promise<void> {
        return this.http.post<any>(`${this.apiUrl}/cil/unsplash/add-to-tenant-library`, unsplashImages).toPromise();
    }

    uploadImage(payload: File, imageId: string): Observable<any> {
        const data = new UnsplashImageUploadCommand(imageId, payload).formData;
        return this.http.post<IModuleImageQuery>(`${this.apiUrl}/cil/unsplash/upload`, data).pipe(map((query) => new ModuleImage(query)));
    }

    addModuleImage(moduleImageId: string, moduleId: string, moduleType: string): Observable<ModuleImage> {
        const data = { image: { id: moduleImageId } };
        if (moduleType === 'e_learning') {
            return this.http.put<IModuleQuery>(`${this.apiUrl}/e-learning/modules/${moduleId}`, data).pipe(map((query) => new ModuleImage(query.image)));
        } else if (moduleType === 'basic') {
            return this.http.put<IModuleQuery>(`${this.apiUrl}/modules/${moduleId}`, data).pipe(map((query) => new ModuleImage(query.image)));
        }
    }
}
